import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight, Building2, Trees, Store, Crown, Home as HomeIcon, TrendingUp,
  MapPin, BadgeCheck, ShieldCheck, LineChart, Headset, FileCheck2, Download,
  CalendarCheck, Calculator, Phone, Ruler, LandPlot,
} from "lucide-react";
import api from "../lib/api";
import { formatINR, formatPriceRange } from "../lib/format";
import { useApp } from "../context/AppContext";
import SectionHeading from "../components/SectionHeading";
import PropertyCard from "../components/PropertyCard";
import Hero from "../components/home/Hero";
import DeveloperLogo from "../components/DeveloperLogo";
import SwipeRow from "../components/home/SwipeRow";

const CATEGORIES = [
  { label: "Residential", icon: Building2, to: "/properties?category=residential", img: "https://images.unsplash.com/photo-1788129114534-703903c7fef0?q=85&w=800&crop=entropy&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBhcGFydG1lbnQlMjBidWlsZGluZyUyMGV4dGVyaW9yJTIwbHV4dXJ5JTIwc2t5bGluZXxlbnwwfHx8fDE3OTA5NTI4MjN8MA" },
  { label: "Plots & Land", icon: LandPlot, to: "/plots", img: "https://images.pexels.com/photos/7937291/pexels-photo-7937291.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" },
  { label: "Commercial", icon: Store, to: "/commercial", img: "https://images.unsplash.com/photo-1600531529272-023c4b821f14?q=85&w=800&crop=entropy&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwzfHxjb21tZXJjaWFsJTIwb2ZmaWNlJTIwYnVpbGRpbmclMjBnbGFzcyUyMGZhY2FkZXxlbnwwfHx8fDE3OTA5NTI4NzZ8MA" },
  { label: "Luxury Homes", icon: Crown, to: "/properties?category=luxury", img: "https://images.unsplash.com/photo-1721815693498-cc28507c0ba2?q=85&w=800&crop=entropy&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHw3fHxsdXh1cnklMjBtb2Rlcm4lMjB2aWxsYSUyMGV4dGVyaW9yfGVufDB8fHx8MTc5MDk1MjgyM3ww" },
  { label: "Villas", icon: HomeIcon, to: "/properties?property_type=Villa", img: "https://images.pexels.com/photos/29334668/pexels-photo-29334668.png" },
  { label: "Investment", icon: TrendingUp, to: "/invest", img: "https://images.pexels.com/photos/17174767/pexels-photo-17174767.jpeg" },
];

const FEATURED_FILTERS = ["All", "Residential", "Plots", "Commercial", "Luxury", "Investment"];
const PLOT_CATS = ["Residential Plots", "Farm Land", "Investment Land", "Gated Plots", "Commercial Plots", "Villa Plots"];

const TRUST = [
  { icon: BadgeCheck, title: "Verified Listings", desc: "Every listing is reviewed for accuracy so you explore with confidence." },
  { icon: LineChart, title: "Market Intelligence", desc: "Price trends, locality insights and appreciation history in one place." },
  { icon: Headset, title: "Expert Assistance", desc: "Dedicated property advisors to guide you end-to-end, free of cost." },
  { icon: ShieldCheck, title: "Transparent Information", desc: "RERA details, documentation and clear pricing — no hidden surprises." },
];

export default function Home() {
  const navigate = useNavigate();
  const { openLead } = useApp();
  const [cities, setCities] = useState([]);
  const [props, setProps] = useState([]);
  const [projects, setProjects] = useState([]);
  const [investments, setInvestments] = useState([]);
  const [developers, setDevelopers] = useState([]);
  const [articles, setArticles] = useState([]);
  const [featFilter, setFeatFilter] = useState("All");

  useEffect(() => {
    api.cities().then(setCities).catch(() => {});
    api.properties({ limit: 60 }).then(setProps).catch(() => {});
    api.projects().then(setProjects).catch(() => {});
    api.investments().then(setInvestments).catch(() => {});
    api.developers().then(setDevelopers).catch(() => {});
    api.articles().then(setArticles).catch(() => {});
  }, []);

  const filtered = props.filter((p) => {
    if (featFilter === "All") return true;
    if (featFilter === "Plots") return p.category === "plots";
    return p.category === featFilter.toLowerCase();
  }).slice(0, 6);

  const plots = props.filter((p) => p.category === "plots");

  return (
    <div>
      <Hero />

      {/* Categories */}
      <Section>
        <SectionHeading eyebrow="Quick Discovery" title="Explore Properties by Category"
          subtitle="From ready-to-move homes to high-potential land — find the right fit for your goals." />
        <SwipeRow testid="categories-slider" itemClass="w-[46%] sm:w-[31%] md:w-[23%] lg:w-[calc((100%-80px)/6)]">
          {CATEGORIES.map((c) => (
            <Link key={c.label} to={c.to} data-testid={`category-${c.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
              className="group block rounded-2xl overflow-hidden bg-white border border-pbs-line pbs-shadow hover:pbs-shadow-hover transition-shadow">
              <div className="aspect-[4/5] overflow-hidden"><img src={c.img} alt={c.label} loading="lazy" draggable={false} className="w-full h-full object-cover zoom-img" /></div>
              <div className="p-3.5 flex items-center gap-2.5">
                <span className="w-8 h-8 rounded-lg bg-pbs-purple/10 text-pbs-purple flex items-center justify-center shrink-0"><c.icon size={17} /></span>
                <span className="min-w-0"><span className="block text-pbs-dark font-bold text-sm sm:text-base leading-tight truncate">{c.label}</span>
                  <span className="text-pbs-purple text-xs font-semibold flex items-center gap-1 mt-0.5 group-hover:gap-1.5 transition-all">Explore <ArrowRight size={12} /></span></span>
              </div>
            </Link>
          ))}
        </SwipeRow>
      </Section>

      {/* Popular Cities */}
      <Section className="bg-white">
        <SectionHeading eyebrow="Popular Cities" title="Explore Properties Across India"
          subtitle="Dive into established and emerging markets, each with curated inventory and local insights." />
        <SwipeRow testid="cities-slider" itemClass="w-[46%] sm:w-[31%] md:w-[23%] lg:w-[calc((100%-80px)/6)]">
          {cities.map((c) => (
            <Link key={c.slug} to={`/city/${c.slug}`} data-testid={`city-card-${c.slug}`}
              className="group relative block rounded-2xl overflow-hidden aspect-[4/5] pbs-shadow hover:pbs-shadow-hover transition-shadow">
              <img src={c.image} alt={c.name} loading="lazy" draggable={false} className="absolute inset-0 w-full h-full object-cover zoom-img" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
              <div className="absolute inset-0 p-3.5 flex flex-col justify-end">
                <span className="text-white font-bold text-base">{c.name}</span>
                <span className="text-white/75 text-xs">{c.property_count} properties</span>
                <span className="mt-1 inline-flex items-center gap-1 text-pbs-gold text-[11px] font-semibold"><TrendingUp size={11} /> {c.appreciation_pct}% YoY</span>
              </div>
            </Link>
          ))}
        </SwipeRow>
      </Section>

      {/* Featured */}
      <Section>
        <SectionHeading eyebrow="Handpicked" title="Featured Properties"
          action={<Link to="/properties" className="hidden sm:inline-flex items-center gap-1.5 text-pbs-purple font-bold text-sm hover:gap-2.5 transition-all">View All <ArrowRight size={16} /></Link>} />
        <div className="flex gap-2 overflow-x-auto no-scrollbar mb-7 -mt-3">
          {FEATURED_FILTERS.map((f) => (
            <button key={f} data-testid={`featured-filter-${f.toLowerCase()}`} onClick={() => setFeatFilter(f)}
              className={`px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap border transition-colors ${featFilter === f ? "bg-pbs-purple text-white border-pbs-purple" : "bg-white text-pbs-grey border-pbs-line hover:border-pbs-purple hover:text-pbs-purple"}`}>
              {f}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {filtered.map((p) => <PropertyCard key={p.slug} property={p} />)}
        </div>
        {filtered.length === 0 && <p className="text-pbs-grey text-center py-10">Loading properties…</p>}
      </Section>

      {/* New Projects */}
      <Section className="bg-white">
        <SectionHeading eyebrow="Fresh Inventory" title="New & Upcoming Projects"
          action={<Link to="/projects" data-testid="view-all-projects" className="hidden sm:inline-flex items-center gap-1.5 text-pbs-purple font-bold text-sm hover:gap-2.5 transition-all">View All Projects <ArrowRight size={16} /></Link>} />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {projects.slice(0, 3).map((pr) => (
            <div key={pr.slug} data-testid={`project-card-${pr.slug}`} className="bg-white rounded-2xl border border-pbs-line overflow-hidden pbs-shadow hover:pbs-shadow-hover transition-shadow group">
              <div className="relative aspect-[16/10] overflow-hidden">
                <img src={pr.image} alt={pr.name} loading="lazy" className="w-full h-full object-cover zoom-img" />
                <span className="absolute top-3 left-3 bg-pbs-orange text-white text-[11px] font-bold px-2.5 py-1 rounded-md">{pr.status}</span>
                <div className="absolute bottom-3 left-3 w-11 h-11 rounded-lg bg-white shadow flex items-center justify-center font-extrabold text-pbs-purple text-sm">
                  {pr.developer.split(" ").map((w) => w[0]).join("").slice(0, 2)}
                </div>
              </div>
              <div className="p-4">
                <p className="text-[11px] font-semibold text-pbs-grey">{pr.developer}</p>
                <h3 className="font-bold text-lg text-pbs-dark leading-snug">{pr.name}</h3>
                <p className="flex items-center gap-1 text-sm text-pbs-grey mt-0.5"><MapPin size={13} className="text-pbs-purple" /> {pr.locality}, {pr.city}</p>
                <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
                  <Info label="Configuration" value={pr.configuration} />
                  <Info label="Price Range" value={formatPriceRange(pr.price_min, pr.price_max)} />
                  <Info label="Possession" value={pr.possession} />
                  <Info label="RERA" value={pr.rera ? "Registered" : "—"} />
                </div>
                <div className="mt-4 flex gap-2">
                  <Link to="/projects" className="flex-1 text-center text-sm font-bold text-white rounded-lg py-2 pbs-gradient">View Project</Link>
                  <button onClick={() => openLead({ type: "brochure", title: `Brochure: ${pr.name}` })} className="p-2.5 rounded-lg border border-pbs-line text-pbs-purple hover:bg-pbs-purple/5" title="Download Brochure"><Download size={16} /></button>
                  <button onClick={() => openLead({ type: "visit", title: `Visit: ${pr.name}` })} className="p-2.5 rounded-lg border border-pbs-line text-pbs-purple hover:bg-pbs-purple/5" title="Schedule Visit"><CalendarCheck size={16} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* Plots & Land */}
      <Section>
        <div className="rounded-3xl pbs-gradient overflow-hidden">
          <div className="grid lg:grid-cols-2 gap-0">
            <div className="p-8 sm:p-12 text-white">
              <span className="text-xs font-bold uppercase tracking-wider text-pbs-gold">Our Specialty</span>
              <h2 className="mt-2 text-3xl sm:text-4xl font-extrabold">Find Your Perfect Plot</h2>
              <p className="mt-3 text-white/80 leading-relaxed">PlotBySquare is built for plotted developments. Explore gated layouts, farm land and investment land with verified titles and RERA transparency.</p>
              <div className="mt-6 grid grid-cols-2 gap-2.5">
                {PLOT_CATS.map((c) => (
                  <Link key={c} to="/plots" className="glass border border-white/20 rounded-xl px-3.5 py-3 text-sm font-semibold hover:bg-white/15 transition-colors flex items-center gap-2">
                    <Trees size={15} className="text-pbs-gold" /> {c}
                  </Link>
                ))}
              </div>
              <Link to="/plots" data-testid="explore-plots-cta" className="mt-7 inline-flex items-center gap-2 bg-pbs-gold text-pbs-dark font-bold px-6 py-3 rounded-xl hover:bg-white transition-colors">
                Explore Plots <ArrowRight size={17} />
              </Link>
            </div>
            <div className="relative min-h-[280px] lg:min-h-full">
              <img src="https://images.pexels.com/photos/11201060/pexels-photo-11201060.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="Plots" className="absolute inset-0 w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </Section>

      {/* Investment */}
      <Section className="bg-white">
        <SectionHeading eyebrow="Invest" title="Real Estate Opportunities Beyond the Ordinary"
          subtitle="Explore curated investment avenues across asset classes. These are opportunities to research — not guaranteed-return products."
          action={<Link to="/invest" className="hidden sm:inline-flex items-center gap-1.5 text-pbs-purple font-bold text-sm">View All <ArrowRight size={16} /></Link>} />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {investments.slice(0, 6).map((inv) => (
            <div key={inv.id} data-testid={`investment-card-${inv.id}`} className="bg-white rounded-2xl border border-pbs-line overflow-hidden pbs-shadow hover:pbs-shadow-hover transition-shadow group">
              <div className="relative aspect-[16/9] overflow-hidden">
                <img src={inv.image} alt={inv.title} loading="lazy" className="w-full h-full object-cover zoom-img" />
                <span className="absolute top-3 left-3 bg-pbs-purple text-white text-[11px] font-bold px-2.5 py-1 rounded-md">{inv.type}</span>
              </div>
              <div className="p-5">
                <h3 className="font-bold text-lg text-pbs-dark">{inv.title}</h3>
                <p className="text-sm text-pbs-grey mt-1 flex items-center gap-1"><MapPin size={13} className="text-pbs-purple" /> {inv.location}</p>
                <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                  <Info label="Entry Price" value={inv.entry_price} />
                  <Info label="Holding" value={inv.holding_period} />
                  <Info label="Potential Use" value={inv.potential_use} />
                </div>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {inv.highlights.map((h) => <span key={h} className="text-[11px] bg-pbs-bg border border-pbs-line text-pbs-dark/80 px-2 py-0.5 rounded-full">{h}</span>)}
                </div>
                <button onClick={() => openLead({ type: "enquiry", title: `Investment: ${inv.title}` })} className="mt-4 w-full text-center text-sm font-bold text-pbs-purple border border-pbs-purple/30 rounded-lg py-2 hover:bg-pbs-purple/5 transition-colors">Explore Opportunity</button>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* Calculators preview */}
      <Section>
        <div className="rounded-3xl bg-pbs-dark overflow-hidden p-8 sm:p-12 relative">
          <div className="relative grid lg:grid-cols-2 gap-8 items-center">
            <div className="text-white">
              <span className="text-xs font-bold uppercase tracking-wider text-pbs-gold">Smart Tools</span>
              <h2 className="mt-2 text-3xl sm:text-4xl font-extrabold">Plan Your Purchase with Confidence</h2>
              <p className="mt-3 text-white/70 leading-relaxed">Six interactive calculators — EMI, affordability, stamp duty, registration, rental yield and area conversion — to make informed decisions.</p>
              <Link to="/calculators" data-testid="calculators-cta" className="mt-6 inline-flex items-center gap-2 bg-pbs-orange text-white font-bold px-6 py-3 rounded-xl hover:bg-[#e08c0c] transition-colors">
                <Calculator size={18} /> Open Calculators
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {[["EMI", Calculator], ["Affordability", TrendingUp], ["Stamp Duty", FileCheck2], ["Registration", FileCheck2], ["Rental Yield", LineChart], ["Area Convert", Ruler]].map(([label, Icon]) => (
                <Link to="/calculators" key={label} className="glass border border-white/15 rounded-xl p-4 text-white hover:bg-white/10 transition-colors">
                  <Icon size={22} className="text-pbs-gold" />
                  <p className="mt-2 text-sm font-semibold">{label}</p>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </Section>

      {/* Location Intelligence */}
      <Section className="bg-white">
        <SectionHeading eyebrow="Location Intelligence" title="Know Your Market Before You Invest"
          subtitle="Drill down from city to locality to micro-market with price trends, rental yields and infrastructure signals." />
        <SwipeRow testid="market-slider" itemClass="w-[78%] sm:w-[46%] md:w-[31%] lg:w-[calc((100%-48px)/4)]">
          {cities.slice(0, 8).map((c) => (
            <Link key={c.slug} to={`/city/${c.slug}`} data-testid={`market-card-${c.slug}`} className="block rounded-2xl border border-pbs-line bg-white p-5 pbs-shadow hover:pbs-shadow-hover transition-shadow group">
              <div className="flex items-center justify-between">
                <span className="font-bold text-pbs-dark">{c.name}</span>
                <span className="text-pbs-gold"><TrendingUp size={16} /></span>
              </div>
              <p className="mt-2 text-2xl font-extrabold text-pbs-dark font-mono">₹{c.avg_price_sqft.toLocaleString("en-IN")}<span className="text-xs text-pbs-grey font-sans">/sq.ft</span></p>
              <p className="text-xs text-pbs-grey mt-0.5">Avg. price</p>
              <div className="mt-3 pt-3 border-t border-pbs-line flex items-center justify-between text-sm">
                <span className="text-green-600 font-bold">+{c.appreciation_pct}%</span>
                <span className="text-pbs-purple font-semibold flex items-center gap-1 group-hover:gap-2 transition-all">Insights <ArrowRight size={13} /></span>
              </div>
            </Link>
          ))}
        </SwipeRow>
      </Section>

      {/* Developers */}
      <Section>
        <SectionHeading eyebrow="Builders" title="Trusted Developers & Builders"
          action={<Link to="/developers" className="hidden sm:inline-flex items-center gap-1.5 text-pbs-purple font-bold text-sm">Explore Developers <ArrowRight size={16} /></Link>} />
        <div className="space-y-4 overflow-hidden dev-marquee" data-testid="dev-marquee">
          {[developers.filter((_, i) => i % 2 === 0), developers.filter((_, i) => i % 2 === 1)].map((row, r) => row.length > 0 && (
            <div key={r} className="flex w-max" data-testid={`dev-row-${r}`}>
              {[0, 1].map((copy) => (
                <div key={copy} aria-hidden={copy === 1} className={`flex gap-4 pr-4 dev-track ${r ? "dev-track-rev" : ""}`}>
                  {row.map((d) => (
                    <Link to="/developers" key={d.slug} tabIndex={copy ? -1 : 0} data-testid={copy ? undefined : `home-dev-${d.slug}`}
                      className="w-[200px] sm:w-[230px] shrink-0 bg-white rounded-2xl border border-pbs-line p-3 hover:border-pbs-purple/40 transition-colors text-center">
                      <DeveloperLogo dev={d} className="h-20" />
                      <h3 className="mt-2.5 mb-1 font-bold text-sm text-pbs-dark leading-tight truncate">{d.name}</h3>
                    </Link>
                  ))}
                </div>
              ))}
            </div>
          ))}
        </div>
      </Section>

      {/* Sell CTA */}
      <Section className="bg-white">
        <div className="rounded-3xl border border-pbs-line bg-pbs-bg p-8 sm:p-12 text-center">
          <h2 className="text-3xl sm:text-4xl font-extrabold text-pbs-dark">Have a Property to Sell or Rent?</h2>
          <p className="mt-3 text-pbs-grey max-w-xl mx-auto">List as an owner, broker or developer and reach serious, verified buyers across India.</p>
          <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
            {["Owner", "Broker", "Developer"].map((r) => <span key={r} className="text-sm font-semibold bg-white border border-pbs-line px-3.5 py-1.5 rounded-full text-pbs-dark">{r}</span>)}
          </div>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <Link to="/post-property" data-testid="post-property-cta" className="inline-flex items-center gap-2 pbs-gradient text-white font-bold px-6 py-3 rounded-xl">Post Property</Link>
            <button onClick={() => openLead({ type: "callback", title: "Talk to an Expert" })} className="inline-flex items-center gap-2 bg-white border border-pbs-line text-pbs-dark font-bold px-6 py-3 rounded-xl hover:border-pbs-purple hover:text-pbs-purple transition-colors"><Phone size={16} /> Talk to an Expert</button>
          </div>
        </div>
      </Section>

      {/* Insights */}
      <Section>
        <SectionHeading eyebrow="Editorial" title="Real Estate Insights"
          action={<Link to="/insights" className="hidden sm:inline-flex items-center gap-1.5 text-pbs-purple font-bold text-sm">All Articles <ArrowRight size={16} /></Link>} />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {articles.slice(0, 3).map((a) => (
            <Link to="/insights" key={a.slug} className="bg-white rounded-2xl border border-pbs-line overflow-hidden pbs-shadow hover:pbs-shadow-hover transition-shadow group">
              <div className="aspect-[16/9] overflow-hidden"><img src={a.image} alt={a.title} loading="lazy" className="w-full h-full object-cover zoom-img" /></div>
              <div className="p-5">
                <span className="text-[11px] font-bold uppercase tracking-wider text-pbs-magenta">{a.category}</span>
                <h3 className="mt-1.5 font-bold text-lg text-pbs-dark leading-snug line-clamp-2 group-hover:text-pbs-purple transition-colors">{a.title}</h3>
                <p className="mt-2 text-sm text-pbs-grey line-clamp-2">{a.summary}</p>
                <div className="mt-3 flex items-center justify-between text-xs text-pbs-grey">
                  <span>{a.author} · {a.read_time}</span>
                  <span className="text-pbs-purple font-semibold flex items-center gap-1">Read <ArrowRight size={12} /></span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </Section>

      {/* Trust */}
      <Section className="bg-white">
        <SectionHeading align="center" eyebrow="Why Us" title="Why PlotBySquare?"
          subtitle="A serious, modern real-estate platform built around trust, clarity and informed decisions." />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {TRUST.map((t) => (
            <div key={t.title} className="text-center p-6 rounded-2xl border border-pbs-line bg-pbs-bg">
              <div className="mx-auto w-14 h-14 rounded-2xl pbs-gradient text-white flex items-center justify-center"><t.icon size={24} /></div>
              <h3 className="mt-4 font-bold text-pbs-dark">{t.title}</h3>
              <p className="mt-2 text-sm text-pbs-grey leading-relaxed">{t.desc}</p>
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}

const Section = ({ children, className = "" }) => (
  <section className={className}>
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20">{children}</div>
  </section>
);

const Info = ({ label, value }) => (
  <div>
    <p className="text-[11px] text-pbs-grey">{label}</p>
    <p className="font-semibold text-pbs-dark text-sm leading-tight">{value}</p>
  </div>
);
